import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import FanClaimButtons from "../components/FanClaimButtons";
import { FAN_LOGIN_ORIGIN_KEY, FanUser, ensureFanSession, fanEmailVerify, needsFirstTimeScreens } from "../lib/fanAccount";
import "./FanLoginPage.css";
import { safeInternalPath } from "../utils/safeInternalPath";
import { t } from "../i18n";

// /fan/login — where the magic-link email lands. EVERY path in, handled
// (CLAUDE.md error-screen rule), silent recovery first:
//   1. fresh link            → verify → session + device merge → redirect
//   2. dead link, signed in  → the link already did its job (second tab,
//      re-tap, prefetch) — redirect silently, no error screen ever
//   3. dead link, signed out → friendly re-send screen (email field open,
//      Apple/Google right there too — any door recovers)
//   4. no token in the URL   → signed in? redirect : same screen, ask copy
//   5. network/server hiccup → "try again" that actually retries
// The redirect target: the page the fan started from (sessionStorage,
// same-tab flows) — but the emailed link opens a FRESH tab almost every
// time, so with no stored origin (or a "/" one) a signed-in fan lands on
// their profile (/fan), never back on the signed-out landing pitch
// (founder bug). First-timers detour through /fan/name first — the same
// stop the Apple/Google claim flow makes, and how email accounts stop being
// "A fan" on DJ cards.

type Phase = "checking" | "resend" | "netfail";

const FanLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>("checking");
  const [expired, setExpired] = useState(false);
  // The token is consumed single-use server-side; hold it once so a retry
  // after a network blip re-sends the SAME token.
  const tokenRef = useRef<string | null>(null);
  if (tokenRef.current === null) {
    tokenRef.current = new URLSearchParams(window.location.search).get("flt") || "";
  }

  const finish = (fan?: FanUser | null) => {
    let target = "";
    try {
      target = sessionStorage.getItem(FAN_LOGIN_ORIGIN_KEY) || "";
      sessionStorage.removeItem(FAN_LOGIN_ORIGIN_KEY);
    } catch {}
    // See the header comment: fresh-tab (or landing-page) arrivals go to
    // the profile; first-timers get the name stop first.
    if (needsFirstTimeScreens(fan)) target = "/fan/name";
    else if (!target || target === "/") target = "/fan";
    navigate(safeInternalPath(target, "/fan"), { replace: true });
  };

  const run = async () => {
    setPhase("checking");
    const token = tokenRef.current || "";

    if (!token) {
      const fan = await ensureFanSession();
      if (fan) return finish(fan);
      setExpired(false);
      setPhase("resend");
      return;
    }

    try {
      const fan = await fanEmailVerify(token);
      return finish(fan);
    } catch (err: any) {
      if (err?.response?.status === 400) {
        // Link expired or already used. If a session exists on this device
        // the link already did its job — recover silently.
        const fan = await ensureFanSession();
        if (fan) return finish(fan);
        setExpired(true);
        setPhase("resend");
        return;
      }
      setPhase("netfail");
    }
  };

  useEffect(() => {
    run();
    // One sign-in exchange per arrival: run() closes over the URL read at mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (phase === "checking") {
    return (
      <div className="fanlogin">
        <div className="fanlogin-card">
          <div className="fanlogin-emoji" aria-hidden="true">🏆</div>
          <div className="fanlogin-title">{t("fanLoginPage.signingYouIn")}</div>
        </div>
      </div>
    );
  }

  if (phase === "netfail") {
    return (
      <div className="fanlogin">
        <div className="fanlogin-card">
          <div className="fanlogin-emoji" aria-hidden="true">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#8b93a3" strokeWidth="2" strokeLinecap="round">
              <path d="M2 8.5a15 15 0 0 1 20 0" />
              <path d="M5.5 12.5a10 10 0 0 1 13 0" />
              <path d="M9 16.2a5 5 0 0 1 6 0" />
              <circle cx="12" cy="19.5" r="1" fill="#8b93a3" />
            </svg>
          </div>
          <div className="fanlogin-title">{t("fanLoginPage.couldnTCheckYour")}</div>
          <div className="fanlogin-sub">{t("fanLoginPage.looksLikeAConnection")}</div>
          <button type="button" className="fanlogin-retry" onClick={run}>
            {t("fanLoginPage.tryAgain")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fanlogin">
      <div className="fanlogin-card">
        <div className="fanlogin-emoji" aria-hidden="true">🏆</div>
        <div className="fanlogin-title">
          {expired ? t("fanLoginPage.thatLinkHasExpired") : t("fanLoginPage.claimYourSpot")}
        </div>
        <div className="fanlogin-sub">
          {expired
            ? t("fanLoginPage.signInLinksOnly")
            : t("fanLoginPage.yourRequestsOnThe")}
        </div>
        <FanClaimButtons
          initialMode={expired ? "email" : "buttons"}
          onSignedIn={finish}
        />
        <div className="fanlogin-foot">{t("fanLoginPage.requestingASongNever")}</div>
      </div>
    </div>
  );
};

export default FanLoginPage;
